/**
 * Ways to sign in, and the small amount of state that has to survive the
 * redirects between them.
 *
 * One account can have several sign-in methods: any of the OAuth providers
 * below, plus Email (a one-time link sent to the account's address, which
 * every account has). Appwrite links a provider to an existing account by
 * email only when the provider vouches that the email is verified — Google
 * does, Microsoft does not — so a refused provider is connected from inside
 * the app instead, once the user has signed in another way. See AuthCallback.
 *
 * When a provider is refused, the app asks that provider who the user is (the
 * `identify` step below: an OpenID Connect ID token, read only for its email),
 * emails a sign-in link to that address, and connects the provider once the
 * link has been used. The email is not trusted for anything: all it does is
 * choose where the link goes, and only that mailbox can use it.
 *
 * Adding a provider: enable it in the Appwrite console for each environment
 * (Auth -> Settings -> OAuth2 Providers), then add it to OAUTH_PROVIDERS. The
 * login page and signup both read this list. Give it an `identify` entry if
 * Appwrite can refuse it (it does not vouch for emails); without one, a refused
 * user is asked to type their email instead.
 */

import React from 'react';
import { Mail } from 'lucide-react';

export const GoogleIcon = ({ className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export const MicrosoftIcon = ({ className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 23 23" className={className} aria-hidden="true">
    <path fill="#f25022" d="M0 0h11v11H0z"/>
    <path fill="#00a4ef" d="M12 0h11v11H12z"/>
    <path fill="#7fba00" d="M0 12h11v11H0z"/>
    <path fill="#ffb900" d="M12 12h11v11H12z"/>
  </svg>
);

const EmailIcon = ({ className = 'h-5 w-5' }) => <Mail className={className} aria-hidden="true" />;

/**
 * The Microsoft app registration's Application (client) ID — the same one
 * entered in Appwrite's Microsoft provider. Public: it is in every Microsoft
 * sign-in URL. Set at build time; without it a refused Microsoft user is asked
 * for their email rather than having it read from Microsoft.
 */
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';

/**
 * `id` is the Appwrite provider id, as in OAuthProvider and on an identity.
 * `identify` is how to ask the provider for an ID token directly; see above.
 */
export const OAUTH_PROVIDERS = [
  { id: 'google', label: 'Google', Icon: GoogleIcon },
  {
    id: 'microsoft',
    label: 'Microsoft',
    Icon: MicrosoftIcon,
    identify: MICROSOFT_CLIENT_ID
      ? {
          authorizeUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
          clientId: MICROSOFT_CLIENT_ID,
        }
      : null,
  },
];

export const EMAIL_METHOD = { id: 'email', label: 'Email', Icon: EmailIcon };

export function getOAuthProvider(id) {
  return OAUTH_PROVIDERS.find((provider) => provider.id === id) || null;
}

export function providerLabel(id) {
  return getOAuthProvider(id)?.label || 'that provider';
}

// =====================================================
// Errors Appwrite hands back on a redirect
// =====================================================

/**
 * Appwrite replaces the `error` query parameter of an OAuth failure URL with a
 * JSON object: `{"message": "...", "type": "general_bad_request", "code": 400}`.
 * Older links and our own redirects carry a plain token instead
 * (`authentication_failed`), which comes back as the type.
 */
export function parseRedirectError(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return {
        type: typeof parsed.type === 'string' ? parsed.type : '',
        message: typeof parsed.message === 'string' ? parsed.message : '',
        code: parsed.code ?? null,
      };
    }
  } catch {
    // Not JSON — one of our own tokens.
  }
  return { type: String(raw), message: '', code: null };
}

/**
 * Whether a refused OAuth sign-in is (most likely) Appwrite declining to link
 * an unverified provider email to an account that already has it.
 *
 * Appwrite reports that case as a bare `general_bad_request`, which is not
 * specific to it, so the login page words its recovery panel conditionally
 * ("if you already have an account"). `user_already_exists` is the same
 * refusal on an older code path.
 */
export function isExistingAccountRefusal(error) {
  return ['general_bad_request', 'user_already_exists'].includes(error?.type);
}

/** What to tell someone whose attempt to connect a provider was refused. */
export function connectFailureMessage(providerId, error) {
  const label = providerLabel(providerId);
  if (error?.type === 'user_already_exists') {
    return `That ${label} account is already used by a different PanelGear account, so it can't be connected to this one.`;
  }
  if (error?.type === 'user_oauth2_provider_error') {
    return `${label} didn't complete the sign-in, so nothing was connected.`;
  }
  return `We couldn't connect ${label}. Please try again.`;
}

// =====================================================
// State that outlives a redirect
// =====================================================
//
// localStorage rather than sessionStorage: an emailed sign-in link usually
// opens in a new tab, and a pending connection started on the login page has
// to be found there. Each value expires so a stale one never fires later.

const PENDING_LINK_KEY = 'signin_pending_link';
const RETURN_TO_KEY = 'signin_return_to';
const FLASH_KEY = 'signin_flash';
const PENDING_TTL_MS = 30 * 60 * 1000;
const SHORT_TTL_MS = 10 * 60 * 1000;

function put(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ value, at: Date.now() }));
  } catch {
    // Storage unavailable (private mode, blocked). The flow still works; it
    // just won't pick up where it left off.
  }
}

/** Read and remove. Expired or unreadable values come back as null. */
function take(key, ttlMs, now = Date.now()) {
  try {
    const raw = localStorage.getItem(key);
    localStorage.removeItem(key);
    if (!raw) return null;
    const { value, at } = JSON.parse(raw);
    if (typeof at !== 'number' || now - at > ttlMs) return null;
    return value ?? null;
  } catch {
    return null;
  }
}

/** Connect this provider as soon as the user is signed in. */
export function setPendingLink(providerId) {
  if (getOAuthProvider(providerId)) put(PENDING_LINK_KEY, providerId);
}

export function takePendingLink(now) {
  const providerId = take(PENDING_LINK_KEY, PENDING_TTL_MS, now);
  return getOAuthProvider(providerId) ? providerId : null;
}

/** Same-origin paths only — never somewhere off the site. */
export function safeReturnPath(path) {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') ? path : null;
}

export function setReturnTo(path) {
  const safe = safeReturnPath(path);
  if (safe) put(RETURN_TO_KEY, safe);
}

export function takeReturnTo(now) {
  return safeReturnPath(take(RETURN_TO_KEY, SHORT_TTL_MS, now));
}

/** A message to show once the next page has loaded. */
export function setFlash(type, message) {
  put(FLASH_KEY, { type, message });
}

export function takeFlash(now) {
  const flash = take(FLASH_KEY, SHORT_TTL_MS, now);
  return flash && typeof flash.message === 'string' ? flash : null;
}

// =====================================================
// Messages for the login page
// =====================================================
//
// Handed over in storage rather than the URL, so the address bar stays plain
// /login and a refresh shows the normal page instead of the same error again.
// The page reads the notice and then clears it (two steps, so React's
// development double render reads the same notice twice instead of losing it).

const LOGIN_NOTICE_KEY = 'signin_login_notice';

/**
 * `{ type: 'error', code }`, `{ type: 'link-sent', provider, email }` or
 * `{ type: 'link-email', provider }` (we need their email to send the link).
 */
export function setLoginNotice(notice) {
  put(LOGIN_NOTICE_KEY, notice);
}

export function readLoginNotice(now = Date.now()) {
  try {
    const raw = localStorage.getItem(LOGIN_NOTICE_KEY);
    if (!raw) return null;
    const { value, at } = JSON.parse(raw);
    if (typeof at !== 'number' || now - at > SHORT_TTL_MS) return null;
    return value && typeof value.type === 'string' ? value : null;
  } catch {
    return null;
  }
}

export function clearLoginNotice() {
  try {
    localStorage.removeItem(LOGIN_NOTICE_KEY);
  } catch {
    // Nothing to clear.
  }
}

const LOGIN_ERRORS = {
  authentication_failed: 'Authentication failed. Please contact your administrator if you need access.',
  no_user_record: 'No user account found. Please contact your administrator to set up access.',
  no_active_organizations: 'You are not active in any organizations. Please contact your administrator.',
  email_link_invalid: 'That sign-in link has expired or has already been used. Please request a new one.',
};

export function loginErrorMessage(code) {
  return LOGIN_ERRORS[code] || 'Sign-in did not complete. Please try again.';
}

// =====================================================
// Asking a refused provider for the user's email
// =====================================================
//
// An OpenID Connect request for an ID token, returned in the URL fragment.
// The token's signature is not checked: the email in it only decides where a
// sign-in link is sent, which anyone can already do from the login page, and
// the link only works for whoever reads that mailbox. `state` ties the answer
// to the request this browser made and `nonce` ties the token to it.

const IDENTIFY_KEY = 'signin_identify';
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function randomToken() {
  try {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

/**
 * The URL that asks `providerId` who the user is, or null when the provider
 * has no `identify` entry (or the browser cannot make a random state).
 */
export function startIdentify(providerId, redirectUri) {
  const identify = getOAuthProvider(providerId)?.identify;
  const state = randomToken();
  const nonce = randomToken();
  if (!identify || !state || !nonce) return null;

  put(IDENTIFY_KEY, { provider: providerId, state, nonce });
  const params = new URLSearchParams({
    client_id: identify.clientId,
    response_type: 'id_token',
    response_mode: 'fragment',
    redirect_uri: redirectUri,
    scope: 'openid profile email',
    state,
    nonce,
  });
  return `${identify.authorizeUrl}?${params}`;
}

/** Whether a URL fragment is a provider answering startIdentify(). */
export function isIdentifyResponse(fragment) {
  return fragment.has('state') && (fragment.has('id_token') || fragment.has('error'));
}

/** The claims of a JWT, without verifying it. Null if it is not one. */
export function decodeJwtPayload(token) {
  try {
    const part = String(token).split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const json = decodeURIComponent(
      Array.from(atob(base64), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
    );
    const claims = JSON.parse(json);
    return claims && typeof claims === 'object' ? claims : null;
  } catch {
    return null;
  }
}

/**
 * The address to send the link to. `preferred_username` first: it is the
 * sign-in name, which is what Appwrite matched the existing account on.
 */
export function emailFromClaims(claims) {
  for (const value of [claims?.preferred_username, claims?.email]) {
    if (typeof value === 'string' && EMAIL_SHAPE.test(value.trim())) return value.trim().toLowerCase();
  }
  return null;
}

/**
 * Read the provider's answer. `{ provider, email }` on success, otherwise
 * `{ provider, error }` — provider is null when the answer does not match a
 * request this browser made.
 */
export function finishIdentify(fragment, now) {
  const request = take(IDENTIFY_KEY, SHORT_TTL_MS, now);
  if (!request || !request.state || fragment.get('state') !== request.state) {
    return { provider: null, error: 'state_mismatch' };
  }
  const provider = request.provider;
  if (fragment.get('error')) return { provider, error: fragment.get('error') };

  const claims = decodeJwtPayload(fragment.get('id_token'));
  const clientId = getOAuthProvider(provider)?.identify?.clientId;
  if (!claims || claims.nonce !== request.nonce || (clientId && claims.aud !== clientId)) {
    return { provider, error: 'invalid_token' };
  }

  const email = emailFromClaims(claims);
  return email ? { provider, email } : { provider, error: 'no_email' };
}
